import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
// styling
import './HomePage.css';
import { Box, Button, Typography, useTheme, Tooltip, Alert } from '@mui/material';
import { animate, scroll } from 'motion';
// components
import ScrollIndicator from '../components/ScrollIndicator';
import AboutSection from '../components/AboutSection';
import AddressLink from '../components/AddressLink';
// assets
import IGD_wordmark from '../assets/IGD_wordmark.png';
import pattern from '../assets/AdobeStock_159213901.jpeg';
import Commercial from '../assets/Third_Export.mp4';

const HomePage = () => {
  const theme = useTheme();
  useEffect(() => {
    // animate progress bar
    const progressEl = document.querySelector('.progress');
    if (progressEl) {
      scroll(
        animate(progressEl, { scaleX: [0, 1] }, { ease: 'linear' })
      );
    }

    // make the pattern float up/down as you scroll the sections
    const patternEl = document.querySelector('.pattern-overlay');
    scroll(
      animate(patternEl, { y: [-100, 0] }, { ease: 'linear' }),
      { offset: [0, 1] } // map entire page scroll 0→100% to y:0→50px
    );

    // animate header small parallax
    document.querySelectorAll('.img-container').forEach(section => {
      const header = section.querySelector('h2');
      if (header) {
        scroll(
          animate(header, { y: [-200, 200] }, { ease: 'linear' }),
          { target: section }
        );
      }
    });
  }, []);

  return (
    <>
      {/* Pattern overlay */}
      <Box
        className="pattern-overlay"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${pattern})`,
          backgroundRepeat: 'repeat',
          opacity: 0.35,
          pointerEvents: 'none',
          zIndex: -1,
        }}
      />

      <section className="img-container" style={{ backgroundColor: '#193e2d' }}>
        <div className='video-container'>
          <Box sx={{ color: '#e6dcbb', textAlign: 'center' }}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ fontFamily: 'lulo-one', fontSize: '1.75rem', color: '#fff' }}
            >
              Welcome to
            </Typography>
            <Box component="img" src={IGD_wordmark} alt="Indy Golf Den" sx={{ maxHeight: 150, width: 'auto' }} />
          </Box>

          <Box mt={2} textAlign="center">
            <AddressLink />
          </Box>

          <Box
            sx={{
              width: '100%',
              maxWidth: { xs: 360, sm: 420, md: 760 },
              mx: 'auto',
              mt: { xs: 2, md: 3 },
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: theme.shadows[4],
              // No fixed height—use aspect ratio to prevent overflow
              aspectRatio: { xs: "16 / 9", md: '16 / 9' },
              maxHeight: { xs: 220, sm: 260, md: 300 },
            }}
          >
            <Box
              component="video"
              src={Commercial}
              autoPlay
              loop
              muted
              playsInline
              // Ensure the video fills the wrapper cleanly
              sx={{
                display: 'block',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>

          <Typography
            variant="h6"
            align="center"
            sx={{
              mt: 2,
              fontWeight: 800,
              letterSpacing: "0.05em",
              color: '#eeeeee'
            }}
          >
            Carmel’s Most Affordable Private Golf Simulator
          </Typography>

          <Box sx={{ mt: 4 }}>
            <Typography variant="body2" sx={{ color: "#e6dcbb", opacity: 0.9, fontFamily: 'Roboto', mt: { xs: 2, md: 4 } }}>
              {/* <strong>Hours:</strong> Wed 6–9pm · Thu 6–9pm · Fri 6–9pm · Sat 9am–10pm · Sun 9am–10pm */}
            </Typography>
            {/* <Typography variant="caption" sx={{ color: "#e6dcbb", opacity: 0.8, fontFamily: 'Roboto' }}>
              <strong>Holiday hours:</strong> Dec 31 & Jan 1 — 10am–9pm
            </Typography> */}
          </Box>

          <Box mt={{ xs: 2.5, md: 4 }} textAlign="center">
            <Button
              variant="contained"
              component={Link}
              to="/booking"
              sx={{ background: '#e6dcbb', color: '#193e2d', fontFamily: 'lulo-one', py: 1.5, px: 4 }}
            >
              Book Now
            </Button>
          </Box>

          <Box mt={4} textAlign="center">
            <ScrollIndicator />
          </Box>
        </div>
      </section>

      <section className="img-container">
        <AboutSection />
      </section>

      <section className="img-container">
        <Box
          component="section"
          sx={{
            position: 'relative',
            py: { xs: 6, md: 12 },
            px: { xs: 3, md: 10 },
            display: 'flex',
            justifyContent: 'center',
            textAlign: 'left',
          }}
        >
          <Box maxWidth="600px">
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                fontFamily: 'lulo-one',
                color: '#193e2d',
                fontSize: { xs: '2.5rem', md: '4rem' },
              }}
            >
              Book Your Session Today!
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontFamily: 'Roboto', color: 'text.secondary' }}>
              Whether you’re looking to practice, play, or take a lesson, Indy Golf Den offers everything you
              need for an exceptional golfing experience. Our advanced technology, private atmosphere, and
              one-on-one focus create the perfect setting for golfers of all skill levels.
            </Typography>
            <Alert severity="success" sx={{ mt: 3, fontWeight: 600 }}>
              Private sessions for just $36/hour. BYOB. No memberships required.
            </Alert>
            <Button
              variant="contained"
              component={Link}
              to="/booking"
              sx={{
                background: '#193e2d',
                color: '#e6dcbb',
                fontFamily: 'lulo-one',
                py: 1.5,
                px: 4,
                mt: 2,
              }}
            >
              Book Now
            </Button>
          </Box>
        </Box>
      </section>

      <section className="img-container" style={{ backgroundColor: "#0f231a" }}>
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: { xs: 2, md: 6 },
            py: { xs: 6, md: 10 },
          }}
        >
          {/* Main footer panel */}
          <Box
            sx={{
              width: "100%",
              maxWidth: 1100,
              mx: "auto",
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid rgba(230,220,187,0.18)",
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
              backdropFilter: "blur(6px)",
              boxShadow: (t) => t.shadows[6],
            }}
          >
            {/* Header row */}
            <Box
              sx={{
                px: { xs: 3, md: 5 },
                py: { xs: 3, md: 4 },
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: "lulo-one",
                    color: "#fff",
                    lineHeight: 1.1,
                    fontSize: { xs: "1.4rem", md: "1.9rem" },
                  }}
                >
                  Indy Golf Den
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#e6dcbb", opacity: 0.9, mt: 0.75, fontFamily: "Roboto" }}
                >
                  Play More • Pay Less • $36/hr • BYOB
                </Typography>
              </Box>

              <Button
                variant="contained"
                component={Link}
                to="/booking"
                sx={{
                  background: "#e6dcbb",
                  color: "#193e2d",
                  fontFamily: "lulo-one",
                  px: 3,
                  py: 1.25,
                  borderRadius: 999,
                  "&:hover": { background: "#d9cfad" },
                }}
              >
                Book Now
              </Button>
            </Box>

            <Box sx={{ height: 1, backgroundColor: "rgba(230,220,187,0.14)" }} />

            {/* Columns */}
            <Box
              sx={{
                px: { xs: 3, md: 5 },
                py: { xs: 3, md: 4 },
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1.2fr 1fr 1fr" },
                gap: { xs: 3, md: 4 },
              }}
            >
              {/* Visit */}
              <Box>
                <Typography
                  sx={{ color: "#fff", fontFamily: "lulo-one", fontSize: "0.95rem", mb: 1 }}
                >
                  Visit
                </Typography>

                {/* Use your existing AddressLink component if you want */}
                <Box mt={{ xs: 2, md: 2 }}>
                  <AddressLink />
                </Box>

                <Typography variant="body2" sx={{ color: "#e6dcbb", opacity: 0.9, fontFamily: "Roboto", mt: 2 }}>
                  <strong className='hours-footer'>Hours: <br /></strong> Mon–Wed Closed <br /> Thu 5:30–9pm <br /> Fri 5:30–9pm <br /> Sat 10am–10pm <br /> Sun 10am–10pm
                </Typography>
              </Box>

              {/* Contact */}
              <Box>
                <Typography
                  sx={{ color: "#fff", fontFamily: "lulo-one", fontSize: "0.95rem", mb: 2 }}
                >
                  Contact
                </Typography>

                <Typography variant="body2" sx={{ color: "#e6dcbb", opacity: 0.9, fontFamily: "Roboto" }}>
                  <strong>Phone:</strong>{" "}
                  <Tooltip
                    title="Text only — no calls"
                    arrow
                    placement="top"
                  >
                    <Box
                      component="a"
                      // href="tel:+13179832224"
                      sx={{ color: "#e6dcbb", textUnderlineOffset: "3px" }}
                    >
                      (317) 983-2224
                    </Box>
                  </Tooltip>
                </Typography>

                <Typography variant="caption" sx={{ color: "#d89936", opacity: 0.9, fontFamily: "Roboto" }}>
                  * Phone Number is TEXT ONLY
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ color: "#e6dcbb", opacity: 0.9, mt: 1, fontFamily: "Roboto" }}
                >
                  <strong>Email:</strong>{" "}
                  <Box
                    component="a"
                    href="mailto:info@indygolfden.com"
                    sx={{ color: "#e6dcbb", textDecoration: "underline", textUnderlineOffset: "3px" }}
                  >
                    info@indygolfden.com
                  </Box>
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ color: "#e6dcbb", opacity: 0.85, mt: 1.25, fontFamily: "Roboto" }}
                >
                  For fastest booking help, include your preferred date/time in the email.
                </Typography>
              </Box>

              {/* Social / Policies */}
              <Box>
                <Typography
                  sx={{ color: "#fff", fontFamily: "lulo-one", fontSize: "0.95rem", mb: 2 }}
                >
                  Connect
                </Typography>

                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
                  {/* <Button
                    variant="outlined"
                    size="small"
                    component="a"
                    href="https://instagram.com/"
                    target="_blank"
                    rel="noreferrer"
                    sx={{
                      borderColor: "rgba(230,220,187,0.45)",
                      color: "#e6dcbb",
                      borderRadius: 999,
                      fontFamily: "Roboto",
                      textTransform: "none",
                      "&:hover": { borderColor: "rgba(230,220,187,0.75)" },
                    }}
                  >
                    Instagram
                  </Button> */}
                  <Button
                    variant="outlined"
                    size="small"
                    component="a"
                    href="https://www.facebook.com/profile.php?id=61584850979908"
                    target="_blank"
                    rel="noreferrer"
                    sx={{
                      borderColor: "rgba(230,220,187,0.45)",
                      color: "#e6dcbb",
                      borderRadius: 999,
                      fontFamily: "Roboto",
                      textTransform: "none",
                      "&:hover": { borderColor: "rgba(230,220,187,0.75)" },
                    }}
                  >
                    Facebook
                  </Button>
                  {/* <Button
                    variant="outlined"
                    size="small"
                    component={Link}
                    to="/policies"
                    sx={{
                      borderColor: "rgba(230,220,187,0.45)",
                      color: "#e6dcbb",
                      borderRadius: 999,
                      fontFamily: "Roboto",
                      textTransform: "none",
                      "&:hover": { borderColor: "rgba(230,220,187,0.75)" },
                    }}
                  >
                    Policies
                  </Button> */}
                </Box>

                <Typography variant="body2" sx={{ color: "#e6dcbb", opacity: 0.85, fontFamily: "Roboto" }}>
                  <strong>Parking:</strong> Use the lot behind the building when available.
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Bottom line (kept visually “footer-ish” within the fullpage section) */}
          <Typography
            variant="caption"
            sx={{
              mt: { xs: 3, md: 4 },
              textAlign: "center",
              color: "rgba(230,220,187,0.75)",
              fontFamily: "Roboto",
            }}
          >
            © {new Date().getFullYear()} Indy Golf Den • All rights reserved
          </Typography>
        </Box>
      </section>

      <div className="progress" />
    </>
  );
};

export default HomePage;